// Terms of Service Page
// Publicly accessible page for Twilio compliance and legal requirements

import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, ArrowLeft, FileText, Mail, Phone } from 'lucide-react';

const TermsOfServicePage = () => {
  const lastUpdated = "April 20, 2026";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              IntakePal
            </span>
          </Link>
          <Link 
            to="/" 
            className="text-sm text-slate-600 hover:text-teal-600 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Terms of Service
              </h1>
              <p className="text-slate-500 text-sm">Last updated: {lastUpdated}</p>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-slate max-w-none">
            <p className="text-lg text-slate-600 mb-8">
              Welcome to IntakePal. These Terms of Service (&quot;Terms&quot;) govern your access to and use of IntakePal&apos;s client intake automation platform (&quot;Service&quot;). By accessing or using our Service, you agree to be bound by these Terms.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 mb-4">
              By creating an account or using IntakePal, you agree to these Terms and our Privacy Policy. If you are using the Service on behalf of a law firm or organization, you represent that you have the authority to bind that entity to these Terms.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. Description of Service</h2>
            <p className="text-slate-600 mb-4">
              IntakePal provides a client intake automation platform for law firms, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Embeddable chat widget for website visitor intake</li>
              <li>Automated lead prioritization and scoring</li>
              <li>Automated follow-up communications via email and SMS</li>
              <li>Lead management dashboard</li>
              <li>CMS integration and data export</li>
              <li>Retainer agreement generation</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. Account Registration</h2>
            <p className="text-slate-600 mb-4">To use IntakePal, you must:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Promptly update any changes to your information</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
            <p className="text-slate-600 mb-4">
              You must be at least 18 years old and have the legal authority to enter into these Terms.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. SMS and Email Messaging Terms</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-medium text-amber-900 mb-3">Important Messaging Consent</h3>
              <p className="text-amber-800 mb-4">
                By using IntakePal&apos;s automated messaging features, you acknowledge and agree:
              </p>
              <ul className="list-disc pl-6 text-amber-800 space-y-2 mb-4">
                <li><strong>Consent Collection:</strong> You are responsible for obtaining proper consent from individuals before sending them automated messages through our platform.</li>
                <li><strong>Message Content:</strong> You will ensure all messages comply with applicable laws, including the Telephone Consumer Protection Act (TCPA) and CAN-SPAM Act.</li>
                <li><strong>Opt-Out Compliance:</strong> You will honor all opt-out requests promptly.</li>
                <li><strong>Message Frequency:</strong> Automated follow-ups are limited to 4 messages per intake submission.</li>
                <li><strong>Carrier Fees:</strong> Message and data rates may apply to recipients. You will inform recipients of this.</li>
              </ul>
              <p className="text-amber-800">
                <strong>Opt-Out Instructions:</strong> All SMS messages include opt-out instructions. Recipients can reply STOP at any time to unsubscribe from future messages.
              </p>
            </div>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">5. Subscription and Payment</h2>
            <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">Pricing</h3>
            <p className="text-slate-600 mb-4">
              IntakePal is offered at <strong>$179.99 per month</strong> with a <strong>7-day free trial</strong>. Pricing is subject to change with 30 days notice.
            </p>
            
            <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">Billing</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Subscriptions are billed monthly in advance</li>
              <li>Payment is processed securely through Stripe</li>
              <li>You authorize us to charge your payment method on file</li>
              <li>Failed payments may result in service suspension</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">Cancellation</h3>
            <p className="text-slate-600 mb-4">
              You may cancel your subscription at any time. Upon cancellation:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Service continues until the end of your current billing period</li>
              <li>No refunds are provided for partial months</li>
              <li>You may export your data before cancellation</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">6. Acceptable Use</h2>
            <p className="text-slate-600 mb-4">You agree NOT to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Use the Service for any unlawful purpose</li>
              <li>Send spam, unsolicited messages, or messages to purchased lists</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use the Service to collect information for illegal purposes</li>
              <li>Resell or redistribute the Service without authorization</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">7. Intellectual Property</h2>
            <p className="text-slate-600 mb-4">
              IntakePal and its original content, features, and functionality are owned by IntakePal and are protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-slate-600 mb-4">
              You retain ownership of all data you submit to the Service. By using IntakePal, you grant us a limited license to process and store your data as necessary to provide the Service.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">8. Data and Privacy</h2>
            <p className="text-slate-600 mb-4">
              Your use of IntakePal is also governed by our <Link to="/privacy" className="text-teal-600 hover:underline">Privacy Policy</Link>. You are responsible for:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Complying with applicable data protection laws</li>
              <li>Obtaining necessary consents from individuals whose data you process</li>
              <li>Ensuring the accuracy of data entered into the system</li>
              <li>Maintaining appropriate confidentiality of client information</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">9. Third-Party Services</h2>
            <p className="text-slate-600 mb-4">
              IntakePal integrates with third-party services including Stripe (payments), Twilio (SMS), and SendGrid (email). Your use of these services is subject to their respective terms and policies.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">10. Disclaimer of Warranties</h2>
            <p className="text-slate-600 mb-4">
              THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.
            </p>
            <p className="text-slate-600 mb-4">
              IntakePal is a technology platform and does not provide legal advice. The automated case summaries and lead scores are for informational purposes only and should not be relied upon as legal assessments.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">11. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, INTAKEPAL SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES.
            </p>
            <p className="text-slate-600 mb-4">
              Our total liability for any claims arising from or related to the Service shall not exceed the amount you paid us in the 12 months preceding the claim.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">12. Indemnification</h2>
            <p className="text-slate-600 mb-4">
              You agree to indemnify and hold harmless IntakePal, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Messages sent through the platform on your behalf</li>
              <li>Your compliance with applicable legal and ethical rules</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">13. Attorney Professional Responsibility</h2>
            <p className="text-slate-600 mb-4">
              <strong>Compliance with Rules of Professional Conduct:</strong> You are solely responsible for ensuring your use of IntakePal complies with the applicable Rules of Professional Conduct in your jurisdiction(s), including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Confidentiality (ABA Rule 1.6):</strong> You acknowledge that we implement reasonable security measures, but you remain responsible for ensuring appropriate safeguards for client information.</li>
              <li><strong>Supervision (ABA Rule 5.3):</strong> You are responsible for supervising the use of this technology in your practice.</li>
              <li><strong>Competence (ABA Rule 1.1):</strong> You should understand the technology&apos;s capabilities and limitations.</li>
              <li><strong>Communication (ABA Rule 1.4):</strong> Automated messages sent through our platform remain your responsibility to ensure appropriateness.</li>
            </ul>
            <p className="text-slate-600 mb-4">
              IntakePal is a technology tool to assist your practice. All legal determinations, client communications, and case decisions must be made by licensed attorneys exercising independent professional judgment.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">14. SMS/Text Message Terms (TCPA Compliance)</h2>
            <p className="text-slate-600 mb-4">
              By using IntakePal&apos;s SMS features, you agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Only send messages to individuals who have provided prior express consent</li>
              <li>Honor all opt-out requests immediately</li>
              <li>Not use the SMS feature for marketing or promotional messages without explicit consent</li>
              <li>Comply with the Telephone Consumer Protection Act (TCPA) and all applicable state laws</li>
              <li>Maintain records of consent for a minimum of 5 years</li>
            </ul>
            <p className="text-slate-600 mb-4">
              IntakePal collects SMS consent through our intake forms on your behalf. You are responsible for ensuring this consent is adequate for your intended use under applicable law.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">15. Termination</h2>
            <p className="text-slate-600 mb-4">
              We may suspend or terminate your access to the Service at any time for:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Violation of these Terms</li>
              <li>Non-payment of fees</li>
              <li>Fraudulent or illegal activity</li>
              <li>At our discretion with reasonable notice</li>
            </ul>
            <p className="text-slate-600 mb-4">
              Upon termination, your right to use the Service ceases immediately. You may request export of your data within 30 days of termination.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">16. Governing Law</h2>
            <p className="text-slate-600 mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law provisions.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">17. Changes to Terms</h2>
            <p className="text-slate-600 mb-4">
              We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms and updating the &quot;Last updated&quot; date. Your continued use of the Service after changes constitutes acceptance of the modified Terms.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">18. Contact Information</h2>
            <p className="text-slate-600 mb-4">
              For questions about these Terms, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6">
              <p className="text-slate-700 mb-2"><strong>IntakePal</strong></p>
              <p className="text-slate-600 flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4" /> support@intakepal.com
              </p>
              <p className="text-slate-600 flex items-center gap-2">
                <Phone className="w-4 h-4" /> (555) 123-4567
              </p>
            </div>

            <div className="mt-8 p-6 bg-teal-50 rounded-xl border border-teal-200">
              <p className="text-teal-800 text-sm">
                By using IntakePal, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-slate-500">
          <p>&copy; {new Date().getFullYear()} IntakePal. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/privacy" className="hover:text-teal-600">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-teal-600">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
